import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Paper,
  CircularProgress,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Tabs,
  Tab,
  Checkbox,
  IconButton,
  Tooltip,
  Divider,
  Stack,
  Collapse
} from '@mui/material';
import {
  Science as ScienceIcon,
  Storage as StorageIcon,
  AccessTime as AccessTimeIcon,
  CloudDownload as DownloadIcon,
  Folder as ProjectIcon,
  Star as TargetIcon,
  CalendarMonth as CalendarIcon,
  ExpandMore as ExpandMoreIcon,
  ChevronRight as ChevronRightIcon
} from '@mui/icons-material';
import ObservatoryIcon from '../components/icons/ObservatoryDomeIcon';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import apiService from '../services/apiService';
import { useFileDownload } from '../hooks/useFileDownload';

const StatCard = ({ title, value, subtitle, icon: Icon, color = 'primary', compact = false }) => (
  <Card>
    <CardContent sx={compact ? { p: 1.25, '&:last-child': { pb: 1.25 } } : undefined}>
      <Box display="flex" justifyContent="space-between" alignItems="flex-start">
        <Box>
          <Typography
            color="textSecondary"
            gutterBottom
            variant="body2"
            sx={compact ? { fontSize: '0.7rem' } : undefined}
          >
            {title}
          </Typography>
          <Typography
            variant={compact ? 'h6' : 'h4'}
            component="div"
            sx={compact ? { my: 0.5, fontSize: '1.05rem', fontWeight: 600 } : { my: 1 }}
          >
            {value}
          </Typography>
          {subtitle && (
            <Typography
              variant="body2"
              color="textSecondary"
              sx={compact ? { fontSize: '0.65rem' } : undefined}
            >
              {subtitle}
            </Typography>
          )}
        </Box>
        <Box
          sx={{
            bgcolor: `${color}.main`,
            color: 'white',
            borderRadius: compact ? 1 : 2,
            p: compact ? 0.75 : 1.5,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}
        >
          <Icon sx={compact ? { fontSize: 18 } : undefined} />
        </Box>
      </Box>
    </CardContent>
  </Card>
);

const TAB_CONFIG = [
  {
    label: 'By Observatory',
    icon: ObservatoryIcon,
    endpoint: '/v1/user-files/by-observatory',
    nameKey: 'observatory_name',
    groupType: 'observatory',
    rowKey: r => (r._id || r.observatory_id || '').toString()
  },
  {
    label: 'By Project',
    icon: ProjectIcon,
    endpoint: '/v1/user-files/by-project',
    nameKey: 'project_name',
    groupType: 'project',
    rowKey: r => (r._id || r.project_id || '').toString()
  },
  {
    label: 'By Target',
    icon: TargetIcon,
    endpoint: '/v1/user-files/by-target',
    nameKey: 'target_name',
    groupType: 'target',
    rowKey: r => (r._id || r.target_name || '').toString()
  },
  {
    label: 'By Date',
    icon: CalendarIcon,
    endpoint: '/v1/user-files/by-date',
    nameKey: '_id',
    groupType: 'date',
    rowKey: r => (r._id || '').toString()
  }
];

const formatBytes = (bytes) => {
  if (!bytes) return '0 B';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  return `${(bytes / (1024 * 1024 * 1024)).toFixed(2)} GB`;
};

const MyUsage = () => {
  // Floating-window pop-out from the avatar Usage menu adds
  // ?standalone=1; switch the StatCards into a compact rendering
  // mode so the four-up grid + tables comfortably fit a 1100x760
  // window.
  const isStandalone = typeof window !== 'undefined'
    && new URLSearchParams(window.location.search).has('standalone');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [timeRange, setTimeRange] = useState('30');
  const [tab, setTab] = useState(0);
  const [tabData, setTabData] = useState({});
  const [tabLoading, setTabLoading] = useState(false);
  const [groupSelected, setGroupSelected] = useState({}); // { [tabIdx]: Set<rowKey> } — full-group selections
  const [expanded, setExpanded] = useState({}); // { [tabIdx]: Set<rowKey> }
  const [groupFiles, setGroupFiles] = useState({}); // { [tabIdx]: { [rowKey]: file[] } }
  const [filesLoading, setFilesLoading] = useState({}); // { [tabIdx]: Set<rowKey> }
  const [fileSelected, setFileSelected] = useState({}); // { [tabIdx]: { [rowKey]: Set<fileId> } }
  const [dateFrom, setDateFrom] = useState(null);
  const [dateTo, setDateTo] = useState(null);

  const { downloadFiles, downloadGroup, downloadProgress, getDownloadUrl } = useFileDownload();

  const dateParams = useMemo(() => {
    if (tab === 3 && (dateFrom || dateTo)) {
      return {
        startDate: dateFrom ? dateFrom.toISOString() : undefined,
        endDate: dateTo ? dateTo.toISOString() : undefined
      };
    }
    const days = parseInt(timeRange, 10);
    const endDate = new Date();
    const startDate = new Date();
    startDate.setDate(startDate.getDate() - days);
    return {
      startDate: startDate.toISOString(),
      endDate: endDate.toISOString()
    };
  }, [tab, timeRange, dateFrom, dateTo]);

  const fetchTab = useCallback(async (tabIdx) => {
    const cfg = TAB_CONFIG[tabIdx];
    setTabLoading(true);
    setError(null);
    try {
      const res = await apiService.get(cfg.endpoint, { params: { ...dateParams } });
      if (res.data?.success) {
        const groups = res.data.data.groups || [];
        setTabData(prev => ({ ...prev, [tabIdx]: groups }));
      }
    } catch (err) {
      const msg = typeof err.response?.data?.error === 'string'
        ? err.response.data.error
        : err.response?.data?.error?.message || err.message || 'Failed to load data';
      setError(msg);
    } finally {
      setTabLoading(false);
    }
  }, [dateParams]);

  useEffect(() => {
    setLoading(true);
    fetchTab(tab).finally(() => setLoading(false));
  }, [tab, fetchTab]);

  const handleTabChange = (_, newValue) => setTab(newValue);

  const groups = tabData[tab] || [];
  const cfg = TAB_CONFIG[tab];
  const tabGroupSelected = groupSelected[tab] || new Set();
  const tabExpanded = expanded[tab] || new Set();
  const tabGroupFiles = groupFiles[tab] || {};
  const tabFilesLoading = filesLoading[tab] || new Set();
  const tabFileSelected = fileSelected[tab] || {};

  const fetchGroupFiles = useCallback(async (rowKey) => {
    if (tabGroupFiles[rowKey]) return;
    setFilesLoading(prev => {
      const next = new Set(prev[tab] || []);
      next.add(rowKey);
      return { ...prev, [tab]: next };
    });
    try {
      const res = await apiService.get('/v1/user-files/group-files', {
        params: { groupType: cfg.groupType, groupId: rowKey }
      });
      if (res.data?.success) {
        const files = res.data.data.files || [];
        setGroupFiles(prev => ({
          ...prev,
          [tab]: { ...(prev[tab] || {}), [rowKey]: files }
        }));
      }
    } catch (err) {
      console.error('Failed to load group files:', err);
    } finally {
      setFilesLoading(prev => {
        const next = new Set(prev[tab] || []);
        next.delete(rowKey);
        return { ...prev, [tab]: next };
      });
    }
  }, [tab, tabGroupFiles, cfg]);

  const toggleExpand = (rowKey) => {
    setExpanded(prev => {
      const cur = new Set(prev[tab] || []);
      if (cur.has(rowKey)) cur.delete(rowKey);
      else {
        cur.add(rowKey);
        fetchGroupFiles(rowKey);
      }
      return { ...prev, [tab]: cur };
    });
  };

  const toggleGroup = (rowKey) => {
    setGroupSelected(prev => {
      const cur = new Set(prev[tab] || []);
      if (cur.has(rowKey)) cur.delete(rowKey);
      else cur.add(rowKey);
      return { ...prev, [tab]: cur };
    });
    // Selecting/deselecting a group clears any per-file selections for that group
    setFileSelected(prev => {
      const tabMap = { ...(prev[tab] || {}) };
      delete tabMap[rowKey];
      return { ...prev, [tab]: tabMap };
    });
  };

  const toggleAll = () => {
    setGroupSelected(prev => {
      const cur = prev[tab] || new Set();
      if (cur.size === groups.length && groups.length > 0) {
        return { ...prev, [tab]: new Set() };
      }
      return { ...prev, [tab]: new Set(groups.map(g => cfg.rowKey(g))) };
    });
    setFileSelected(prev => ({ ...prev, [tab]: {} }));
  };

  const toggleFile = (rowKey, fileId) => {
    setFileSelected(prev => {
      const tabMap = { ...(prev[tab] || {}) };
      const cur = new Set(tabMap[rowKey] || []);
      if (cur.has(fileId)) cur.delete(fileId);
      else cur.add(fileId);
      tabMap[rowKey] = cur;
      return { ...prev, [tab]: tabMap };
    });
    // Selecting individual files implies the whole-group checkbox is no longer the right level
    setGroupSelected(prev => {
      const cur = new Set(prev[tab] || []);
      cur.delete(rowKey);
      return { ...prev, [tab]: cur };
    });
  };

  // Stats — sum across (a) fully-selected groups + (b) per-file selections.
  // When nothing is selected, fall back to all groups in the tab so the
  // cards still show a meaningful total.
  const summary = useMemo(() => {
    const anyGroup = tabGroupSelected.size > 0;
    const anyFiles = Object.values(tabFileSelected).some(s => s && s.size > 0);
    if (!anyGroup && !anyFiles) {
      const obs = groups.reduce((s, g) => s + (g.observation_count || 0), 0);
      const files = groups.reduce((s, g) => s + (g.total_files || 0), 0);
      const bytes = groups.reduce((s, g) => s + (g.total_size || 0), 0);
      const expSec = groups.reduce((s, g) => s + (g.total_exposure_seconds || 0), 0);
      return { observations: obs, files, gb: bytes / 1073741824, hours: expSec / 3600 };
    }

    let obs = 0, files = 0, bytes = 0, expSec = 0;
    const obsIds = new Set();
    for (const g of groups) {
      const key = cfg.rowKey(g);
      if (tabGroupSelected.has(key)) {
        obs += (g.observation_count || 0);
        files += (g.total_files || 0);
        bytes += (g.total_size || 0);
        expSec += (g.total_exposure_seconds || 0);
      } else {
        const sel = tabFileSelected[key];
        if (sel && sel.size > 0) {
          const groupFileList = tabGroupFiles[key] || [];
          for (const f of groupFileList) {
            const fid = (f.file_id || f._id || '').toString();
            if (sel.has(fid)) {
              files += 1;
              bytes += (f.file_size_bytes || 0);
              expSec += (f.exposure_time || 0);
              if (f.observation_id) obsIds.add(f.observation_id.toString());
            }
          }
        }
      }
    }
    obs += obsIds.size;
    return { observations: obs, files, gb: bytes / 1073741824, hours: expSec / 3600 };
  }, [groups, tabGroupSelected, tabFileSelected, tabGroupFiles, cfg]);

  const totalSelectedCount = useMemo(() => {
    let c = tabGroupSelected.size;
    for (const s of Object.values(tabFileSelected)) {
      if (s && s.size > 0) c += s.size;
    }
    return c;
  }, [tabGroupSelected, tabFileSelected]);

  const handleDownloadRow = (rowKey) => downloadGroup(cfg.groupType, rowKey);

  const handleDownloadSelected = async () => {
    // Whole-group downloads
    for (const rowKey of tabGroupSelected) {
      // eslint-disable-next-line no-await-in-loop
      await downloadGroup(cfg.groupType, rowKey);
    }
    // Per-file downloads (one batch per group)
    for (const [rowKey, fileSet] of Object.entries(tabFileSelected)) {
      if (!fileSet || fileSet.size === 0) continue;
      if (tabGroupSelected.has(rowKey)) continue; // already covered by group download
      // eslint-disable-next-line no-await-in-loop
      await downloadFiles(Array.from(fileSet));
    }
  };

  const handleDownloadFile = (fileId) => {
    const url = getDownloadUrl(fileId);
    const a = document.createElement('a');
    a.href = url;
    a.target = '_blank';
    a.rel = 'noopener';
    a.click();
  };

  if (loading && !groups.length) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box p={3}>
        <Alert severity="error">{error}</Alert>
        <Button onClick={() => fetchTab(tab)} sx={{ mt: 2 }}>Retry</Button>
      </Box>
    );
  }

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4">Usage</Typography>
        <FormControl sx={{ minWidth: 200 }}>
          <InputLabel>Time Range</InputLabel>
          <Select
            value={timeRange}
            onChange={(e) => setTimeRange(e.target.value)}
            label="Time Range"
          >
            <MenuItem value="7">Last 7 days</MenuItem>
            <MenuItem value="30">Last 30 days</MenuItem>
            <MenuItem value="90">Last 90 days</MenuItem>
            <MenuItem value="365">Last year</MenuItem>
          </Select>
        </FormControl>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <Grid container spacing={3} mb={3}>
        <Grid item xs={12} md={3}>
          <StatCard
            title="Observations"
            value={summary.observations.toLocaleString()}
            subtitle={totalSelectedCount > 0 ? `${totalSelectedCount} item${totalSelectedCount === 1 ? '' : 's'} selected` : 'across all groups'}
            icon={ScienceIcon}
            color="primary"
            compact={isStandalone}
          />
        </Grid>
        <Grid item xs={12} md={3}>
          <StatCard
            title="Telescope Time"
            value={`${summary.hours.toFixed(1)}h`}
            subtitle="shutter open"
            icon={AccessTimeIcon}
            color="info"
            compact={isStandalone}
          />
        </Grid>
        <Grid item xs={12} md={3}>
          <StatCard
            title="Exposures"
            value={summary.files.toLocaleString()}
            subtitle="files uploaded"
            icon={StorageIcon}
            color="success"
            compact={isStandalone}
          />
        </Grid>
        <Grid item xs={12} md={3}>
          <StatCard
            title="Data Stored"
            value={`${summary.gb.toFixed(2)} GB`}
            subtitle={null}
            icon={StorageIcon}
            color="secondary"
            compact={isStandalone}
          />
        </Grid>
      </Grid>

      <Divider sx={{ mb: 2 }} />

      <Paper sx={{ p: 2, mb: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
          <Tabs value={tab} onChange={handleTabChange} variant="scrollable" scrollButtons="auto">
            {TAB_CONFIG.map(c => (
              <Tab key={c.label} icon={<c.icon />} iconPosition="start" label={c.label} />
            ))}
          </Tabs>

          <Stack direction="row" spacing={1} alignItems="center">
            {tab === 3 && (
              <>
                <DatePicker
                  label="From"
                  value={dateFrom}
                  onChange={setDateFrom}
                  slotProps={{ textField: { size: 'small', sx: { width: 150 } } }}
                />
                <DatePicker
                  label="To"
                  value={dateTo}
                  onChange={setDateTo}
                  slotProps={{ textField: { size: 'small', sx: { width: 150 } } }}
                />
              </>
            )}
            <Button
              variant="contained"
              size="small"
              startIcon={<DownloadIcon />}
              disabled={totalSelectedCount === 0 || downloadProgress.status !== 'idle'}
              onClick={handleDownloadSelected}
            >
              Download Selected ({totalSelectedCount})
            </Button>
          </Stack>
        </Box>
      </Paper>

      {tabLoading ? (
        <Box display="flex" justifyContent="center" py={4}>
          <CircularProgress />
        </Box>
      ) : groups.length === 0 ? (
        <Alert severity="info">No usage data found for the selected period.</Alert>
      ) : (
        <Paper sx={{ p: 2 }}>
          {/* Header row */}
          <Box display="flex" alignItems="center" px={1} py={0.5} sx={{ borderBottom: 1, borderColor: 'divider' }}>
            <Box sx={{ width: 32 }} />
            <Checkbox
              size="small"
              indeterminate={tabGroupSelected.size > 0 && tabGroupSelected.size < groups.length}
              checked={tabGroupSelected.size > 0 && tabGroupSelected.size === groups.length}
              onChange={toggleAll}
              sx={{ mr: 1 }}
            />
            <Typography variant="body2" sx={{ flex: 1, fontWeight: 600 }}>{cfg.label.replace('By ', '')}</Typography>
            <Typography variant="body2" sx={{ width: 110, textAlign: 'right', fontWeight: 600 }}>Observations</Typography>
            <Typography variant="body2" sx={{ width: 90, textAlign: 'right', fontWeight: 600 }}>Files</Typography>
            <Typography variant="body2" sx={{ width: 90, textAlign: 'right', fontWeight: 600 }}>Hours</Typography>
            <Typography variant="body2" sx={{ width: 110, textAlign: 'right', fontWeight: 600 }}>Size</Typography>
            <Box sx={{ width: 56 }} />
          </Box>

          {groups.map((g) => {
            const rowKey = cfg.rowKey(g);
            const isGroupChecked = tabGroupSelected.has(rowKey);
            const isExpanded = tabExpanded.has(rowKey);
            const isLoadingFiles = tabFilesLoading.has(rowKey);
            const files = tabGroupFiles[rowKey] || [];
            const fileSel = tabFileSelected[rowKey] || new Set();
            const groupHasFileSel = fileSel.size > 0;
            const name = g[cfg.nameKey] || (cfg.groupType === 'date' ? g._id : 'Unknown');
            return (
              <React.Fragment key={rowKey}>
                <Box
                  display="flex"
                  alignItems="center"
                  px={1}
                  py={1}
                  sx={{
                    borderBottom: 1,
                    borderColor: 'divider',
                    bgcolor: isGroupChecked ? 'action.selected' : (groupHasFileSel ? 'action.hover' : 'transparent'),
                    '&:hover': { bgcolor: 'action.hover' }
                  }}
                >
                  <IconButton size="small" sx={{ width: 32, height: 32 }} onClick={() => toggleExpand(rowKey)}>
                    {isExpanded ? <ExpandMoreIcon fontSize="small" /> : <ChevronRightIcon fontSize="small" />}
                  </IconButton>
                  <Checkbox
                    size="small"
                    checked={isGroupChecked}
                    indeterminate={!isGroupChecked && groupHasFileSel}
                    onChange={() => toggleGroup(rowKey)}
                    sx={{ mr: 1 }}
                  />
                  <Typography variant="body2" sx={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {name}
                  </Typography>
                  <Typography variant="body2" sx={{ width: 110, textAlign: 'right' }}>
                    {(g.observation_count || 0).toLocaleString()}
                  </Typography>
                  <Typography variant="body2" sx={{ width: 90, textAlign: 'right' }}>
                    {(g.total_files || 0).toLocaleString()}
                  </Typography>
                  <Typography variant="body2" sx={{ width: 90, textAlign: 'right' }}>
                    {((g.total_exposure_seconds || 0) / 3600).toFixed(1)}
                  </Typography>
                  <Typography variant="body2" sx={{ width: 110, textAlign: 'right' }}>
                    {formatBytes(g.total_size || 0)}
                  </Typography>
                  <Box sx={{ width: 56, textAlign: 'right' }}>
                    <Tooltip title="Download all files in group">
                      <span>
                        <IconButton
                          size="small"
                          onClick={() => handleDownloadRow(rowKey)}
                          disabled={downloadProgress.status !== 'idle'}
                        >
                          <DownloadIcon fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>
                  </Box>
                </Box>

                <Collapse in={isExpanded} unmountOnExit>
                  <Box sx={{ pl: 5, pr: 1, py: isExpanded ? 1 : 0, bgcolor: 'action.hover' }}>
                    {isLoadingFiles ? (
                      <Box display="flex" justifyContent="center" py={2}>
                        <CircularProgress size={20} />
                      </Box>
                    ) : files.length === 0 ? (
                      <Typography variant="body2" color="text.secondary" py={1}>
                        No files in this group.
                      </Typography>
                    ) : (
                      files.map((f) => {
                        const fid = (f.file_id || f._id || '').toString();
                        const isFileChecked = isGroupChecked || fileSel.has(fid);
                        return (
                          <Box
                            key={fid}
                            display="flex"
                            alignItems="center"
                            px={1}
                            py={0.5}
                            sx={{
                              borderBottom: 1,
                              borderColor: 'divider',
                              fontSize: '0.85rem'
                            }}
                          >
                            <Checkbox
                              size="small"
                              checked={isFileChecked}
                              disabled={isGroupChecked}
                              onChange={() => toggleFile(rowKey, fid)}
                              sx={{ mr: 1 }}
                            />
                            <Typography variant="body2" sx={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontFamily: 'monospace', fontSize: '0.78rem' }}>
                              {f.file_name || f.filename || fid}
                            </Typography>
                            <Typography variant="caption" sx={{ width: 90, textAlign: 'right', color: 'text.secondary' }}>
                              {f.filter || '—'}
                            </Typography>
                            <Typography variant="caption" sx={{ width: 80, textAlign: 'right', color: 'text.secondary' }}>
                              {(f.exposure_time || 0).toFixed(1)}s
                            </Typography>
                            <Typography variant="caption" sx={{ width: 100, textAlign: 'right', color: 'text.secondary' }}>
                              {formatBytes(f.file_size_bytes || 0)}
                            </Typography>
                            <Box sx={{ width: 56, textAlign: 'right' }}>
                              <Tooltip title="Download file">
                                <IconButton size="small" onClick={() => handleDownloadFile(fid)}>
                                  <DownloadIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </Box>
                          </Box>
                        );
                      })
                    )}
                  </Box>
                </Collapse>
              </React.Fragment>
            );
          })}
        </Paper>
      )}
    </Box>
  );
};

export default MyUsage;
